import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { PrimaryButton, SecondaryButton, DangerButton } from "@/components/Buttons.jsx";
import ToggleSwitch from "@/components/ToggleSwitch.jsx";
import TimePicker from "react-time-picker";
import "react-time-picker/dist/TimePicker.css";
import "./picker.css";
import ModalBasic from "@/components/ModalBasic.jsx";
import Badge from "@/components/Badges.jsx";
import { updateSpecialHours } from "@/api/settings.js";
import { addToast } from "@/store/app/action.js";

const SpecialHoursPanel = ({ tzOffset, storeDetails, onRefresh }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingHour, setEditingHour] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    date: "",
    openTime: "10:00",
    closeTime: "17:00",
    open: true,
    closesNextDay: false,
  });

  // Get special hours from storeDetails prop and add temp IDs for UI tracking
  const specialHours = useMemo(() => {
    const hours = storeDetails?.special_hours || [];
    return hours.map((hour, index) => ({
      ...hour,
      tempId: hour.tempId || `temp_${index}_${Date.now()}`,
    }));
  }, [storeDetails?.special_hours]);

  // Check if any special hours are 2+ months in the future
  const hasFutureDates = useMemo(() => {
    const twoMonthsFromNow = moment().add(2, 'months');
    return specialHours.some(hour => {
      // Parse date from MM/DD/YYYY format
      const dateParts = hour.date.split("/");
      if (dateParts.length === 3) {
        const hourDate = moment(`${dateParts[2]}-${dateParts[0]}-${dateParts[1]}`, "YYYY-MM-DD");
        // Check if date is 2+ months in the future
        return hourDate.isAfter(twoMonthsFromNow);
      }
      return false;
    });
  }, [specialHours]);

  const openAddModal = () => {
    setEditingHour(null);
    setFormData({
      name: "",
      date: "",
      openTime: "10:00",
      closeTime: "17:00",
      open: true,
      closesNextDay: false,
    });
    setShowModal(true);
  };

  const openEditModal = (hour) => {
    setEditingHour(hour);
    // Parse date from MM/DD/YYYY to YYYY-MM-DD for input
    const dateParts = hour.date.split("/");
    const formattedDate = dateParts.length === 3 
      ? `${dateParts[2]}-${dateParts[0].padStart(2, "0")}-${dateParts[1].padStart(2, "0")}`
      : "";

    // Parse hours from HHMM-HHMM to HH:mm
    // Handle hours that might be split across days (e.g., "2200-2400" or "0000-0200")
    const hoursParts = hour.hours.split("-");
    let openTime = "10:00";
    let closeTime = "17:00";
    let closeHHmm = "";
    let closesNextDay = false;
    
    if (hoursParts.length === 2) {
      const openHHmm = hoursParts[0];
      closeHHmm = hoursParts[1];
      
      // If close time is 2400, it means it closes at midnight (end of day)
      // If open time is 0000 and close is not 2400, it might be next day hours
      // For display, we'll show the actual times
      if (openHHmm && openHHmm !== "0000") {
        openTime = moment(openHHmm, "HHmm").add(tzOffset * -1, "minutes").format("HH:mm");
      } else if (openHHmm === "0000") {
        openTime = "00:00";
      }
      
      if (closeHHmm && closeHHmm !== "2400" && closeHHmm !== "0000") {
        closeTime = moment(closeHHmm, "HHmm").add(tzOffset * -1, "minutes").format("HH:mm");
      } else if (closeHHmm === "2400") {
        closeTime = "24:00"; // This will be handled specially
      } else if (closeHHmm === "0000") {
        closeTime = "00:00";
      }
      
      // If we have a case like "2200-2400", we want to show it as closing at midnight
      // But if it's "2200-0200", we need to detect that it spills into next day
      const openLocal = moment(openHHmm, "HHmm");
      const closeLocal = moment(closeHHmm === "2400" ? "2400" : closeHHmm, "HHmm");
      
      // If close time is before open time (not counting 2400), it spills into next day
      if (closeHHmm !== "2400" && closeLocal.isBefore(openLocal)) {
        // This is a next-day scenario, but for editing we'll show the actual close time
        closeTime = moment(closeHHmm, "HHmm").add(tzOffset * -1, "minutes").format("HH:mm");
        closesNextDay = true;
      } else if (closeHHmm === "2400") {
        closesNextDay = true;
      }
    }

    // Also check based on the formatted times
    const opensLocal = moment(openTime === "24:00" ? "00:00" : openTime, "HH:mm");
    const closesLocal = moment(closeTime === "24:00" ? "00:00" : closeTime, "HH:mm");
    if (!closesNextDay) {
      closesNextDay = opensLocal.isAfter(closesLocal);
    }
    
    setFormData({
      name: hour.name || "",
      date: formattedDate,
      openTime: openTime === "24:00" ? "00:00" : openTime,
      closeTime: closeTime === "24:00" ? "00:00" : closeTime,
      open: hour.open !== undefined ? hour.open : true,
      closesNextDay: closesNextDay,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      dispatch(
        addToast({
          message: "Please enter a name for the special hour",
          type: "error",
          timer: 5,
        })
      );
      return;
    }

    if (!formData.date) {
      dispatch(
        addToast({
          message: "Please select a date",
          type: "error",
          timer: 5,
        })
      );
      return;
    }

    if (formData.open && (!formData.openTime || !formData.closeTime)) {
      dispatch(
        addToast({
          message: "Please enter both open and close times",
          type: "error",
          timer: 5,
        })
      );
      return;
    }

    // Format date from YYYY-MM-DD to MM/DD/YYYY
    const dateParts = formData.date.split("-");
    const formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;

    // Check for date conflicts
    const conflictingHour = specialHours.find(h => {
      // Skip the hour we're currently editing
      if (editingHour && h.tempId === editingHour.tempId) {
        return false;
      }
      // Check if dates match
      return h.date === formattedDate;
    });

    if (conflictingHour) {
      dispatch(
        addToast({
          message: `A special hour already exists for ${formatDisplayDate(formattedDate)}. Please edit or delete the existing entry first.`,
          type: "error",
          timer: 5,
        })
      );
      return;
    }

    setLoading(true);
    try {

      // Format hours from HH:mm to HHMM-HHMM
      // Handle hours that spill into next day (same logic as regular hours)
      let formattedHours = "";
      if (formData.open) {
        const opensat = moment(formData.openTime, "HH:mm")
          .add(tzOffset, "minutes")
          .format("HHmm");
        const closesat = moment(formData.closeTime, "HH:mm")
          .add(tzOffset, "minutes")
          .format("HHmm");

        // If close time is before open time, it means hours spill into next day
        // Store as HHMM-2400 format (same as regular hours)
        if (closesat < opensat) {
          formattedHours = `${opensat}-2400`;
        } else {
          formattedHours = `${opensat}-${closesat}`;
        }
      } else {
        formattedHours = "0000-0000";
      }

      const updatedHour = {
        name: formData.name.trim(),
        date: formattedDate,
        hours: formattedHours,
        open: formData.open,
      };

      // Build the updated array
      let updatedSpecialHours = [...specialHours];
      
      if (editingHour) {
        // Update existing hour - find by tempId (UI tracking)
        const index = updatedSpecialHours.findIndex(h => h.tempId === editingHour.tempId);
        
        if (index !== -1) {
          // Update the hour, preserving _id and tempId
          updatedSpecialHours[index] = {
            ...updatedSpecialHours[index],
            ...updatedHour,
            _id: updatedSpecialHours[index]._id, // Preserve _id if it exists
            tempId: updatedSpecialHours[index].tempId, // Preserve tempId for UI
          };
        } else {
          // Item not found - this shouldn't happen, but log for debugging
          console.error("Could not find special hour to update:", editingHour);
          dispatch(
            addToast({
              message: "Error: Could not find the special hour to update",
              type: "error",
              timer: 5,
            })
          );
          setLoading(false);
          return;
        }
      } else {
        // Add new hour - add tempId for UI tracking
        updatedSpecialHours.push({
          ...updatedHour,
          tempId: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        });
      }

      // Strip tempId before sending to server (keep only _id and data fields)
      const hoursForServer = updatedSpecialHours.map(({ tempId, ...hour }) => hour);

      await updateSpecialHours(hoursForServer);
      setShowModal(false);
      // Refresh store details in parent component
      if (onRefresh) {
        await onRefresh();
      }
      dispatch(
        addToast({
          message: editingHour ? "Special hour updated successfully" : "Special hour added successfully",
          type: "success",
          timer: 5,
        })
      );
    } catch (error) {
      console.error("Error saving special hour:", error);
      dispatch(
        addToast({
          message: "Error saving special hour, please try again later",
          type: "error",
          timer: 5,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (hour) => {
    if (!window.confirm(`Are you sure you want to delete "${hour.name}"?`)) {
      return;
    }

    setLoading(true);
    try {
      // Remove the hour from the array using tempId (UI tracking)
      const updatedSpecialHours = specialHours.filter(h => h.tempId !== hour.tempId);
      
      // Strip tempId before sending to server
      const hoursForServer = updatedSpecialHours.map(({ tempId, ...hour }) => hour);
      
      await updateSpecialHours(hoursForServer);
      // Refresh store details in parent component
      if (onRefresh) {
        await onRefresh();
      }
      dispatch(
        addToast({
          message: "Special hour deleted successfully",
          type: "success",
          timer: 5,
        })
      );
    } catch (error) {
      console.error("Error deleting special hour:", error);
      dispatch(
        addToast({
          message: "Error deleting special hour, please try again later",
          type: "error",
          timer: 5,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDisplayDate = (dateStr) => {
    // Convert MM/DD/YYYY to a more readable format
    const dateParts = dateStr.split("/");
    if (dateParts.length === 3) {
      const date = new Date(dateParts[2], dateParts[0] - 1, dateParts[1]);
      return moment(date).format("MMMM D, YYYY");
    }
    return dateStr;
  };

  const formatDisplayHours = (hoursStr) => {
    if (!hoursStr || hoursStr === "0000-0000") {
      return "Closed";
    }
    const parts = hoursStr.split("-");
    if (parts.length === 2) {
      const openHHmm = parts[0];
      const closeHHmm = parts[1];
      
      const open = moment(openHHmm, "HHmm").add(tzOffset * -1, "minutes").format("h:mm A");
      
      // Handle close time - check if it spills into next day
      const opensLocal = moment(openHHmm, "HHmm");
      const closesLocal = moment(closeHHmm === "2400" ? "2400" : closeHHmm, "HHmm");
      // Hours spill into next day if: close time is 2400 OR close time is before open time
      const closesNextDay = closeHHmm === "2400" || (closeHHmm !== "2400" && closesLocal.isBefore(opensLocal));
      
      let closeDisplay;
      if (closeHHmm === "2400") {
        closeDisplay = "Midnight";
      } else {
        closeDisplay = moment(closeHHmm, "HHmm").add(tzOffset * -1, "minutes").format("h:mm A");
      }
      
      return `${open} - ${closeDisplay}${closesNextDay ? " (Next Day)" : ""}`;
    }
    return hoursStr;
  };

  // Check if a date is more than 2 months in the future
  const isOutsideTwoMonthWindow = (dateStr) => {
    const dateParts = dateStr.split("/");
    if (dateParts.length === 3) {
      const hourDate = moment(`${dateParts[2]}-${dateParts[0]}-${dateParts[1]}`, "YYYY-MM-DD");
      const twoMonthsFromNow = moment().add(2, 'months');
      return hourDate.isAfter(twoMonthsFromNow);
    }
    return false;
  };

  return (
    <div className="grow">
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl text-slate-800 dark:text-slate-100 font-bold">
            Special Hours (Holidays)
          </h2>
          <PrimaryButton onClick={openAddModal} disabled={loading}>
            Add Special Hours
          </PrimaryButton>
        </div>

        {/* Informational note - only show if there are dates 2+ months in the future */}
        {hasFutureDates && (
          <div className="bg-indigo-50 dark:bg-indigo-400/10 border border-indigo-200 dark:border-indigo-400/20 rounded-lg p-4">
            <div className="flex items-start">
              <div className="text-sm text-indigo-700 dark:text-indigo-300">
                <p>Special hours will automatically appear to customers when the date is within 2 months. Hours set for dates more than 2 months away will remain hidden until they fall within this window.</p>
              </div>
            </div>
          </div>
        )}

        {specialHours.length === 0 ? (
          <div className="text-center py-8 text-slate-500 dark:text-slate-400">
            No special hours configured. Click "Add Special Hours" to create one.
          </div>
        ) : (
          <div className="space-y-4">
            {specialHours.map((hour) => (
              <div
                key={hour.tempId}
                className="border rounded-lg p-4 bg-white dark:bg-theme-surface-accent"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                        {hour.name}
                      </h3>
                      {isOutsideTwoMonthWindow(hour.date) && (
                        <Badge color="yellow" size="xs" slim>
                          Hidden (2+ months away)
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                      {formatDisplayDate(hour.date)}
                    </p>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {hour.open ? formatDisplayHours(hour.hours) : "Closed"}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <SecondaryButton
                      onClick={() => openEditModal(hour)}
                      disabled={loading}
                      size="sm"
                    >
                      Edit
                    </SecondaryButton>
                    <DangerButton
                      onClick={() => handleDelete(hour)}
                      disabled={loading}
                      size="sm"
                    >
                      Delete
                    </DangerButton>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <ModalBasic
        modalOpen={showModal}
        setModalOpen={setShowModal}
        title={editingHour ? "Edit Special Hours" : "Add Special Hours"}
        width="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Name <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              className="form-input w-full"
              placeholder="e.g., Thanksgiving, Christmas, New Year's Day"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Date <span className="text-rose-500">*</span>
            </label>
            <input
              type="date"
              className="form-input w-full"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            />
          </div>

          <div>
            <div className="flex items-center gap-3 mb-3">
              <ToggleSwitch
                checked={formData.open}
                onChange={(checked) => setFormData({ ...formData, open: checked })}
                offLabel="Closed"
                onLabel="Open"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">
                {formData.open ? "Store is open" : "Store is closed"}
              </span>
            </div>
          </div>

          {formData.open && (
            <div className="space-y-4 border-t border-slate-200 dark:border-slate-700 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Opens at ({storeDetails?.tz || "Local Time"})
                  </label>
                  <TimePicker
                    value={formData.openTime}
                    onChange={(value) => {
                      const newFormData = { ...formData, openTime: value };
                      // Check if hours spill into next day
                      const opensLocal = moment(value, "HH:mm");
                      const closesLocal = moment(formData.closeTime, "HH:mm");
                      newFormData.closesNextDay = opensLocal.isAfter(closesLocal);
                      setFormData(newFormData);
                    }}
                    clockIcon={null}
                    disableClock={true}
                    clearIcon={null}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {(() => {
                      const opensLocal = moment(formData.openTime, "HH:mm");
                      const closesLocal = moment(formData.closeTime, "HH:mm");
                      const closesNextDay = opensLocal.isAfter(closesLocal);
                      return `Closes at${closesNextDay ? " (Next Day)" : ""} (${storeDetails?.tz || "Local Time"})`;
                    })()}
                  </label>
                  <TimePicker
                    value={formData.closeTime}
                    onChange={(value) => {
                      const newFormData = { ...formData, closeTime: value };
                      // Check if hours spill into next day
                      const opensLocal = moment(formData.openTime, "HH:mm");
                      const closesLocal = moment(value, "HH:mm");
                      newFormData.closesNextDay = opensLocal.isAfter(closesLocal);
                      setFormData(newFormData);
                    }}
                    clockIcon={null}
                    disableClock={true}
                    clearIcon={null}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <SecondaryButton onClick={() => setShowModal(false)} disabled={loading}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleSave} loading={loading}>
              {editingHour ? "Update" : "Add"} Special Hours
            </PrimaryButton>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
};

export default SpecialHoursPanel;

